/**
 * Agent Registry v2 — normalized schema.
 *
 * Key changes from v1:
 *   - Exactly 12 fields per list row (no name, os, os_version, kernel, location, tags)
 *   - `version` not `agent_version`, `last_seen` not `last_seen_at`
 *   - Summary OS/version blocks carry `percentage` + `distribution[]`
 *   - Detail has `network{}` and `system{}` sub-objects
 *   - Sort uses separate `sort` + `order` params
 *   - agent_id is a ULID (`agt_...`), no hostname fallback on detail
 *   - New platform release endpoints (5a/5b)
 */
import { z } from "zod";

export const agentStatusSchema = z.enum(["active", "inactive"]);
export type AgentStatus = z.infer<typeof agentStatusSchema>;

/** List row — exactly 12 fields per the v2 spec. */
export const registryAgentSchema = z.object({
  agent_id: z.string(),
  hostname: z.string(),
  status: agentStatusSchema,
  private_ip: z.string().nullable().default(null),
  public_ip: z.string().nullable().default(null),
  platform: z.string().nullable().default(null),
  os_family: z.string().default(""),
  arch: z.string().default(""),
  version: z.string().default(""),
  is_latest: z.boolean().default(false),
  enrolled_at: z.string().nullable().default(null),
  last_seen: z.string().nullable().default(null),
});
export type RegistryAgent = z.infer<typeof registryAgentSchema>;

const paginationSchema = z.object({
  page: z.number(),
  per_page: z.number(),
  total: z.number(),
  total_pages: z.number(),
});
export type RegistryPagination = z.infer<typeof paginationSchema>;

const registryMetaSchema = z.object({
  latest_version: z.string().default(""),
  available_os_families: z.array(z.string()).default([]),
  available_versions: z.array(z.string()).default([]),
});
export type RegistryMeta = z.infer<typeof registryMetaSchema>;

/** Note: meta was removed in the latest API update. Use /agents/filters
 *  and /platform/agent-release for filter data and version info. */
export const registryListSchema = z.object({
  data: z.array(registryAgentSchema),
  pagination: paginationSchema,
});
export type RegistryList = z.infer<typeof registryListSchema>;

/* ---------- Picker (lean 4-field list for TopNav selector) ---------- */

export const pickerAgentSchema = z.object({
  agent_id: z.string(),
  hostname: z.string(),
  status: agentStatusSchema,
  platform: z.string().nullable().default(null),
  // private_ip — added to the backend picker response so the TopNav
  // ClientSelector can display + filter by IP without having to hit
  // the full /agents endpoint. Nullable because an agent might not
  // have reported one yet; `.default(null)` keeps older/leaner picker
  // responses that omit the field still parsing cleanly.
  private_ip: z.string().nullable().default(null),
});
export type PickerAgent = z.infer<typeof pickerAgentSchema>;

export const pickerListSchema = z.array(pickerAgentSchema);
export type PickerList = z.infer<typeof pickerListSchema>;

/* ---------- Summary (v2 — with distribution + percentages) ---------- */

const fleetSchema = z.object({
  total: z.number().default(0),
  active: z.number().default(0),
  inactive: z.number().default(0),
});

const osDistEntrySchema = z.object({
  os_family: z.string(),
  count: z.number(),
  percentage: z.number().default(0),
});

const osSummarySchema = z.object({
  distinct_families: z.number().default(0),
  distribution: z.array(osDistEntrySchema).default([]),
});

const versionDistEntrySchema = z.object({
  version: z.string(),
  count: z.number(),
  percentage: z.number().default(0),
  is_latest: z.boolean().default(false),
});

const versionsSummarySchema = z.object({
  latest_version: z.string().default(""),
  on_latest_count: z.number().default(0),
  update_available_count: z.number().default(0),
  distribution: z.array(versionDistEntrySchema).default([]),
});

export const registrySummarySchema = z.object({
  fleet: fleetSchema,
  operating_systems: osSummarySchema,
  versions: versionsSummarySchema,
});
export type RegistrySummary = z.infer<typeof registrySummarySchema>;

/* ---------- Filter facets ---------- */

export const registryFiltersSchema = z.object({
  os_families: z.array(z.string()).default([]),
  versions: z.array(z.string()).default([]),
  statuses: z.array(z.string()).default([]),
});
export type RegistryFilters = z.infer<typeof registryFiltersSchema>;

/* ---------- Detail (v2 — network + system sub-objects) ---------- */

const networkSchema = z.object({
  private_ip: z.string().nullable().default(null),
  public_ip: z.string().nullable().default(null),
  mac_address: z.string().nullable().default(null),
});

const systemSchema = z.object({
  platform: z.string().nullable().default(null),
  os_family: z.string().default(""),
  os_name: z.string().nullable().default(null),
  os_version: z.string().nullable().default(null),
  kernel: z.string().nullable().default(null),
  arch: z.string().default(""),
  cpu_model: z.string().nullable().default(null),
  cpu_cores: z.number().nullable().default(null),
  ram_total_bytes: z.number().nullable().default(null),
  ram_total_gb: z.number().nullable().default(null),
  serial_number: z.string().nullable().default(null),
});

export const registryAgentDetailSchema = z.object({
  agent_id: z.string(),
  hardware_id: z.string().default(""),
  hostname: z.string(),
  org_id: z.string(),
  status: agentStatusSchema,
  version: z.string().default(""),
  is_latest: z.boolean().default(false),
  enrolled_at: z.string().nullable().default(null),
  last_seen: z.string().nullable().default(null),
  last_started_at: z.string().nullable().default(null),
  cert_not_after: z.string().nullable().default(null),
  events_total: z.number().default(0),
  network: networkSchema,
  system: systemSchema,
  system_info_updated_at: z.string().nullable().default(null),
});
export type RegistryAgentDetail = z.infer<typeof registryAgentDetailSchema>;

/* ---------- Platform release (5a/5b) ---------- */

export const agentReleaseSchema = z.object({
  latest_version: z.string(),
  minimum_version: z.string().default(""),
  release_channel: z.string().default("stable"),
  released_at: z.string().nullable().default(null),
  updated_at: z.string().nullable().default(null),
  updated_by: z.string().nullable().default(null),
  release_notes_url: z.string().nullable().default(null),
});
export type AgentRelease = z.infer<typeof agentReleaseSchema>;

/* ---------- Query params ---------- */

export const registrySortFieldSchema = z.enum([
  "hostname", "last_seen", "enrolled_at", "status",
]);
export type RegistrySortField = z.infer<typeof registrySortFieldSchema>;
export type RegistrySortOrder = "asc" | "desc";

export interface RegistryQuery {
  search?: string | undefined;
  status?: "active" | "inactive" | "all" | undefined;
  os_family?: string[] | undefined;
  version?: string[] | undefined;
  sort?: RegistrySortField | undefined;
  order?: RegistrySortOrder | undefined;
  page?: number | undefined;
  per_page?: number | undefined;
}
